import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Globe from "@/components/Globe";
import { MaskedLines } from "@/components/motion";

const FEED = [
  { id: "EVT-9F31", status: "VERIFIED", delta: "Δ12m · Δ1.2min", ok: true },
  { id: "EVT-A204", status: "FLAGGED", delta: "Δ712m · OFF-ROUTE", ok: false },
  { id: "EVT-77BC", status: "VERIFIED", delta: "Δ8m · Δ0.4min", ok: true },
  { id: "EVT-3E19", status: "FLAGGED", delta: "SPOOF SIGNATURE", ok: false },
  { id: "EVT-C552", status: "VERIFIED", delta: "Δ21m · Δ3.1min", ok: true },
];

const TruthFeed = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setIdx((i) => (i + 1) % FEED.length), 2400);
    return () => clearInterval(iv);
  }, []);
  const item = FEED[idx];
  return (
    <div
      data-testid="truth-feed"
      className="absolute bottom-6 left-0 bg-background border border-border px-4 py-3 w-[270px] z-10"
    >
      <p className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground mb-2 flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-truth animate-blink inline-block" />
        LIVE VERIFICATION FEED
      </p>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="font-mono text-xs"
        >
          <span className="text-foreground">{item.id}</span>
          <span className={`ml-2 ${item.ok ? "text-truth" : "text-anomaly"}`}>{item.status}</span>
          <span className="block text-muted-foreground mt-1">{item.delta}</span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const globeY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="hero" className="grain-overlay relative border-b border-border overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-36 pb-24 lg:pt-44 lg:pb-32 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-8"
            >
              Routing-Agnostic Verification · WGS-84 · Immutable Audit
            </motion.p>
            <h1 data-testid="hero-headline" className="font-display font-black tracking-tight leading-[0.92] text-[13.5vw] sm:text-7xl lg:text-[6.2rem]">
              <MaskedLines
                lines={["The Truth Layer", "for Delivery", "Networks."]}
                delay={0.25}
              />
            </h1>
            <motion.p
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 font-mono text-[11px] tracking-[0.25em] uppercase text-foreground/70"
            >
              Delivery Verification Platform — proof for every delivery
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              GeoVerify validates every delivery event, every location, every timestamp, and every
              claim — no matter what routing engine your operation uses.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                data-testid="hero-request-access-btn"
                href="#request-access"
                className="bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-telemetry transition-colors duration-200 flex items-center gap-2"
              >
                Request Access <ArrowUpRight className="w-4 h-4" />
              </a>
              <Link
                data-testid="hero-docs-btn"
                to="/docs"
                className="border border-foreground font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-200 flex items-center gap-2"
              >
                View Developer Docs
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.25, duration: 1 }}
              className="mt-14 grid grid-cols-3 max-w-lg border border-border divide-x divide-border font-mono"
            >
              {[
                ["4", "SIGNALS / EVENT"],
                ["<120ms", "VERIFY LATENCY"],
                ["1", "IMMUTABLE TRAIL"],
              ].map(([v, l]) => (
                <div key={l} className="px-4 py-3">
                  <p className="text-lg font-semibold text-foreground">{v}</p>
                  <p className="text-[10px] tracking-widest text-muted-foreground mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
          <motion.div
            style={{ y: globeY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <Globe size={560} />
            <TruthFeed />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
